import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Mail, RefreshCw, Sparkles, CheckCircle2 } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import logoImg from '../assets/logo_transparent.png';
import teamUpdatingImg from '../assets/team_updating.jpg';

/*
const LinkedinIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>);
const TwitterIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>);
const GithubIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>);
*/

// =========================================================================================
// NOTE: Team member details are preserved below in comments for future update.
// All names and profile details will be updated later.
// =========================================================================================
/*
const team = [
  {
    name: 'Kallesh D R',
    initials: 'KDR',
    role: 'Founder & Technical Lead (CTO)',
    emoji: '⚡',
    bio: 'A visionary technologist with deep expertise in software architecture, AI systems, and product engineering. Kallesh leads all technical strategy at QuBright — from system design to deployment pipelines — ensuring every product is built with precision, scalability, and impact.',
    expertise: ['System Architecture', 'AI & Machine Learning', 'Full Stack Dev', 'Cloud Infrastructure', 'DevOps & CI/CD'],
    color: '#1A3BCC',
    gradient: 'from-blue-600 to-indigo-700',
    bg: 'from-blue-50 to-indigo-50',
    accentBg: '#EEF2FF',
    quote: '"Code is poetry when it solves real problems with elegance."',
  },
  {
    name: 'Geetha D C',
    initials: 'GDC',
    role: 'Founder & Head of Finance & Management',
    emoji: '🌟',
    bio: 'Geetha brings operational brilliance and financial acumen to QuBright, overseeing all business, finance, and management operations. Her leadership ensures the company runs with transparency, discipline, and a people-first culture that enables long-term sustainable growth.',
    expertise: ['Financial Strategy', 'Operations Management', 'Client Relations', 'Business Development', 'HR & Culture'],
    color: '#FF7B00',
    gradient: 'from-orange-500 to-amber-600',
    bg: 'from-orange-50 to-amber-50',
    accentBg: '#FFF7ED',
    quote: '"A great company is built on trust, discipline, and care for its people."',
  },
  {
    name: 'Kallesh D C',
    initials: 'KDC',
    role: 'Founder & Technical Lead',
    emoji: '🚀',
    bio: 'A passionate builder and technical leader who thrives on turning complex ideas into working software. Kallesh D C drives product development and engineering execution at QuBright — mentoring the team, leading architecture decisions, and ensuring delivery excellence on every project.',
    expertise: ['Product Engineering', 'Mobile App Dev', 'Web Platforms', 'Technical Mentorship', 'Agile Delivery'],
    color: '#8B5CF6',
    gradient: 'from-violet-500 to-fuchsia-600',
    bg: 'from-violet-50 to-fuchsia-50',
    accentBg: '#F5F3FF',
    quote: '"Ship fast, iterate faster. Always keep the customer at the center."',
  },
];

function MemberCard({ member, index }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const isCenter = index === 1;

  return (
    <motion.div
      ref={ref}
      initial={{ y: 60, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.75, delay: index * 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`relative flex flex-col rounded-3xl overflow-hidden border shadow-card hover:shadow-royal transition-all duration-500 hover:-translate-y-2 group ${isCenter ? 'lg:scale-[1.04]' : ''}`}
      style={{ borderColor: `${member.color}20` }}
    >
      <div className={`relative bg-gradient-to-br ${member.gradient} p-8 pb-16 overflow-hidden`}>
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-4 -left-4 w-32 h-32 rounded-full bg-black/10 blur-xl" />

        <div className="relative z-10 flex justify-between items-start mb-6">
          <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
            <span className="font-syne font-extrabold text-2xl text-white">{member.initials}</span>
          </div>
          <span className="text-5xl">{member.emoji}</span>
        </div>

        <div className="relative z-10">
          <h3 className="font-syne font-extrabold text-2xl text-white mb-1 leading-tight">{member.name}</h3>
          <p className="font-space text-white/80 text-sm font-semibold">{member.role}</p>
        </div>
      </div>

      <div className="bg-white flex-1 flex flex-col p-7 -mt-8 mx-3 rounded-2xl shadow-sm border border-slate-100 relative z-10">
        <blockquote
          className="font-space text-sm italic font-medium mb-5 pb-5 border-b leading-relaxed"
          style={{ color: member.color, borderColor: `${member.color}20` }}
        >
          {member.quote}
        </blockquote>

        <p className="font-inter text-slate-500 text-sm leading-relaxed mb-6 flex-1">
          {member.bio}
        </p>

        <div className="mb-6">
          <p className="font-space text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Areas of Expertise</p>
          <div className="flex flex-wrap gap-2">
            {member.expertise.map((skill, i) => (
              <span
                key={i}
                className="px-3 py-1 rounded-full text-xs font-space font-semibold"
                style={{ color: member.color, background: member.accentBg, border: `1px solid ${member.color}25` }}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
          <p className="font-space text-xs text-slate-400 font-medium mr-1">Connect</p>
          {[
            { icon: <LinkedinIcon />, href: '#' },
            { icon: <TwitterIcon />, href: '#' },
            { icon: <GithubIcon />, href: '#' },
            { icon: <Mail size={14} />, href: 'mailto:qubrightoffice@gmail.com' },
          ].map((s, i) => (
            <a
              key={i}
              href={s.href}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
              style={{ background: member.accentBg, color: member.color }}
            >
              {s.icon}
            </a>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
*/

const values = [
  { icon: '🎯', title: 'Mission-Driven', desc: 'Every decision we make is anchored to our mission: delivering technology that creates real value.' },
  { icon: '🤝', title: 'Founder-Led', desc: 'Our founders are hands-on — involved in every project, every client conversation, every hire.' },
  { icon: '🧠', title: 'Technical Depth', desc: 'We stay close to the code and the craft, ensuring quality is never an afterthought.' },
  { icon: '💛', title: 'Team First', desc: 'We invest in our people because we know great products are built by great people.' },
];

export default function OurTeam() {
  return (
    <div className="min-h-screen bg-brand-light">
      <SEO
        title="Our Team"
        description="Meet the leadership and team behind QuBright Technologies LLP — builders, innovators, and visionaries driving digital excellence."
        url="https://qubright.web.app/team"
      />
      <Navbar forceOpaque />

      {/* Hero */}
      <section className="relative pt-36 pb-28 overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-brand-blue/20 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-brand-orange/10 blur-[100px] pointer-events-none" />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <span className="section-label mb-6 inline-block">Meet the Team</span>
            <h1 className="font-syne font-extrabold text-5xl md:text-7xl text-white mb-6 leading-tight">
              The People
              <span className="block gradient-text">Behind QuBright</span>
            </h1>
            <p className="font-inter text-blue-200 text-xl leading-relaxed max-w-2xl mx-auto mb-10">
              Passionate builders and visionaries united by a single mission — to build technology that matters,
              train the next generation of developers, and create lasting impact.
            </p>

            {/* QuBright logo lockup */}
            <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20">
              <div className="w-12 h-12 rounded-xl bg-white p-1.5 flex items-center justify-center">
                <img src={logoImg} alt="QuBright" className="w-full h-full object-contain" />
              </div>
              <div className="text-left">
                <p className="font-syne font-bold text-white text-base leading-tight">QuBright Technologies LLP</p>
                <p className="font-space text-brand-orange text-xs italic">"Where Creativity Meets Precision"</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 60L1440 60L1440 20C1200 60 960 0 720 20C480 40 240 0 0 20L0 60Z" fill="#F8FAFC" />
          </svg>
        </div>
      </section>

      {/* Team Profiles Updating Section */}
      <section className="py-24 bg-brand-light relative overflow-hidden">
        {/* Ambient subtle glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[450px] bg-gradient-to-r from-blue-500/10 via-amber-500/10 to-indigo-500/10 blur-[130px] pointer-events-none rounded-full" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/20 text-xs font-space font-bold uppercase tracking-wider mb-5 shadow-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
              </span>
              <span>Updating Under Process</span>
            </div>

            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-4 tracking-tight">
              Leadership & Team Profiles <span className="gradient-text">Under Process</span>
            </h2>
            <p className="font-inter text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
              We are currently revising and updating our leadership roster, executive profiles, and credentials.
              Our updated team profiles will be published here shortly.
            </p>
          </motion.div>

          {/* Feature Showcase Card */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="bg-white rounded-3xl border border-slate-200/90 shadow-royal overflow-hidden hover:shadow-2xl transition-all duration-500"
          >
            <div className="grid lg:grid-cols-12 gap-0 items-stretch">
              {/* Image side */}
              <div className="lg:col-span-7 relative bg-slate-950 flex items-center justify-center overflow-hidden min-h-[340px] lg:min-h-[470px] group">
                <img
                  src={teamUpdatingImg}
                  alt="Team Profiles Updating Under Process"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-95"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/30 pointer-events-none" />

                {/* Floating status tag on the image */}
                <div className="absolute bottom-5 left-5 right-5 sm:right-auto bg-slate-900/85 backdrop-blur-md border border-white/15 px-4 py-3 rounded-2xl flex items-center gap-3 shadow-xl">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-sm shrink-0">
                    <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-syne font-bold text-xs uppercase tracking-wide">Sync Status: In Progress</p>
                    <p className="text-slate-300 text-xs font-inter">Synchronizing team member bios & achievements</p>
                  </div>
                </div>
              </div>

              {/* Status details side */}
              <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col justify-between bg-gradient-to-b from-white to-slate-50/70 border-t lg:border-t-0 lg:border-l border-slate-100">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-blue animate-pulse"></span>
                    <p className="font-space font-bold text-xs text-brand-blue uppercase tracking-wider">Directory Maintenance</p>
                  </div>

                  <h3 className="font-syne font-extrabold text-2xl text-slate-900 mb-3 leading-snug">
                    New Profiles & Details Arriving Soon
                  </h3>
                  <p className="font-inter text-slate-500 text-sm leading-relaxed mb-6">
                    Our team is expanding and transitioning to our next operational tier. We are standardizing verified credentials and leadership insights to bring you an enriched team directory.
                  </p>

                  {/* Checklist items */}
                  <div className="space-y-3.5 mb-8">
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100 hover:border-blue-200 transition-colors">
                      <div className="mt-0.5 w-6 h-6 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-syne font-bold text-xs text-slate-800">Leadership & Founder Bios</p>
                        <p className="font-inter text-xs text-slate-500">Reviewing and updating executive summaries</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100 hover:border-amber-200 transition-colors">
                      <div className="mt-0.5 w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                        <Clock className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-syne font-bold text-xs text-slate-800">Roles & Team Structure</p>
                        <p className="font-inter text-xs text-slate-500">Aligning technical and leadership responsibilities</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100 hover:border-purple-200 transition-colors">
                      <div className="mt-0.5 w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-syne font-bold text-xs text-slate-800">Verified Portfolios</p>
                        <p className="font-inter text-xs text-slate-500">Adding project highlights and social links</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Direct contact CTA */}
                <div className="pt-6 border-t border-slate-200/80">
                  <p className="font-inter text-xs text-slate-500 mb-3 font-medium">
                    Have an urgent query or wish to connect with our leadership?
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="mailto:qubrightoffice@gmail.com"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white font-space font-semibold text-xs hover:bg-blue-700 transition-all shadow-md hover:shadow-lg"
                    >
                      <Mail className="w-3.5 h-3.5" /> Contact Office
                    </a>
                    <Link
                      to="/careers"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-space font-semibold text-xs hover:bg-slate-100 transition-all"
                    >
                      Join QuBright <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 
            TEAM CARDS PLACEHOLDER FOR WHEN PROFILES ARE UNCOMMENTED:
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-start mt-12">
              {team.map((member, i) => (
                <MemberCard key={i} member={member} index={i} />
              ))}
            </div>
          */}
        </div>
      </section>

      {/* Leadership values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-syne font-extrabold text-3xl md:text-4xl text-slate-900 mb-4">
              How We <span className="gradient-text">Lead</span>
            </h2>
            <p className="font-inter text-slate-500 text-base max-w-lg mx-auto">
              Leadership at QuBright is not about titles — it's about ownership, empathy, and constant delivery.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="bg-brand-light rounded-2xl p-6 border border-slate-100 text-center hover:shadow-card transition-all duration-300"
              >
                <div className="text-4xl mb-4">{v.icon}</div>
                <h3 className="font-syne font-bold text-slate-900 text-base mb-2">{v.title}</h3>
                <p className="font-inter text-slate-500 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join the team CTA */}
      <section className="py-24 bg-gradient-to-br from-slate-950 to-blue-950 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />

        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="text-5xl mb-5">👋</div>
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-white mb-5 leading-tight">
              Want to Work With Us?
            </h2>
            <p className="font-inter text-blue-200 text-lg mb-10 leading-relaxed">
              We're always looking for talented engineers, designers, and creators to join the QuBright family.
              Come build something amazing with us.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/careers">
                <button className="px-8 py-4 rounded-xl bg-white text-brand-blue font-space font-bold text-sm hover:bg-blue-50 transition-all duration-200 shadow-lg">
                  View Open Roles →
                </button>
              </Link>
              <a href="mailto:qubrightoffice@gmail.com">
                <button className="px-8 py-4 rounded-xl border border-white/30 text-white font-space font-semibold text-sm hover:bg-white/10 transition-all duration-200">
                  Send Open Application
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
